/**
 * 協力会社のページ /p/:token（工程管理の4回目、#103、docs/39 §6.3.1、Figma D・D2・D3）
 *   ログインのいらない別の枠（アプリの上の帯とメニューは出さない。ログインしている人が開いても、この形で出す＝API はクッキーを読まない）
 *   上の帯：ロゴと「{業者名} 様」→ 藍の帯「今日の予定」・日付・「{会社}から」→ 今日のカード（工程ごと）→ 次の予定（60日先まで）→ 済んだ工程（7日前まで、たたむ）→ 下の一文
 *   今日のカード：「今日」「n日目／n日」（終わりの日は「今日で終わり」）・現場名・工程名・稼働日の区切りの棒・住所と「地図」・現場の担当と「電話」・
 *     ボタン4つ（終わった・雨で中止・遅れる・写真）・送った報告の今の状態（確かめ待ちは「取り消す」）
 *   遅れる（D2）・写真を送る（D3）はシート。写真はこの端末で描き直して小さくしてから送る（表示用 長辺 1600px・小さい絵 400px・JPEG 0.8）
 *   文字 17px・押す所 56px。token は画面の外に出さない（リファラーを送らない・fetch は credentials: omit）
 */
import {
  PARTNER_LINK_LIMITS,
  REPORT_REASONS,
  hmJst,
  mdJst,
  mdOf,
  mdwLongOf,
  mdwOf,
  reportKindText,
  type PartnerPage as PageData,
  type PartnerReport,
  type PartnerTask,
  type ScheduleReportKind,
  type ScheduleReportReason,
} from "@genbaryoku/contract";
import { PARTNER_LINK_MSG, REPORT_REASON_LABEL, stopIcon } from "../schedule/msg.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { CrIcon } from "../../components/Create.jsx";
import { ErrorText, useToast } from "../../components/ui.jsx";
import { fitWithin } from "../../lib/image.js";
import { currentLabels, setCurrentLabels, t as tx } from "../../labels.js";
import { PRELOGIN_PRODUCT, productInfo, setCurrentProduct } from "../../lib/product.js";

/** 公開の API（クッキーを送らない・リファラーを送らない・控えを使わない） */
class PartnerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { title?: string; code?: string; company?: { name: string; phone: string | null } } & Record<string, unknown>,
  ) {
    super(body.title ?? "処理できませんでした");
  }
}
async function papi<T>(method: "GET" | "POST" | "DELETE", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/p/${path}`, {
      method,
      credentials: "omit",
      referrerPolicy: "no-referrer",
      cache: "no-store",
      ...(body === undefined ? {} : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
    });
  } catch {
    throw new PartnerApiError(0, { title: "接続できませんでした。電波の状態を確認して、再度お試しください" });
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) throw new PartnerApiError(res.status, (json && typeof json === "object" ? json : {}) as PartnerApiError["body"]);
  return json as T;
}
/** API の文（{task} などは会社の言葉で埋める。#142 段5） */
const msgOf = (e: unknown) => (e instanceof Error ? tx(e.message) : "処理できませんでした");

/** このページではリファラーを送らない（Caddy も /p/* に Referrer-Policy: no-referrer を付ける。開発の Vite 用に meta も） */
function useNoReferrer() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    const r = document.createElement("meta");
    r.name = "robots";
    r.content = "noindex";
    document.head.append(m, r);
    const title = document.title;
    document.title = PARTNER_LINK_MSG.todayTitle;
    return () => {
      m.remove();
      r.remove();
      document.title = title;
    };
  }, []);
}

export function PartnerLinkPage() {
  const { token = "" } = useParams();
  useNoReferrer();
  const q = useQuery({ queryKey: ["partner-page", token], queryFn: () => papi<PageData>("GET", token), retry: false, refetchOnWindowFocus: true, staleTime: 0 });
  // 会社の言葉と製品（#142 段5）：ログインなしのページは Me が無いので、公開の API の答えから入れる（描く前に。
  // PARTNER_LINK_MSG の {task}・{schedule}・{siteShort}・{pw:stopRain} がこの会社の言葉になる＝営業力なら「作業」「スケジュール」「案件」「中止」）
  if (q.data) {
    setCurrentLabels({ ...currentLabels(), ...q.data.labels });
    setCurrentProduct(q.data.product);
  }
  const P = productInfo(q.data?.product ?? PRELOGIN_PRODUCT);
  return (
    <div className="pl-page" data-testid="partner-page">
      <header className="pl-top">
        <img src={P.assets.logoHeader} alt={P.name} width={320} height={100} />
        {q.data ? (
          <span className="pl-who" data-testid="pl-partner">
            <CrIcon k="user" />
            {PARTNER_LINK_MSG.honorific(q.data.partner.name)}
          </span>
        ) : null}
      </header>
      {q.isPending ? <p className="hint pl-pad">読み込み中…</p> : q.isError ? <Gone error={q.error} /> : <Body token={token} page={q.data} />}
    </div>
  );
}

/** 使えないリンク（410＝会社名と電話／404＝何も出さない） */
function Gone({ error }: { error: unknown }) {
  const e = error instanceof PartnerApiError ? error : null;
  const company = e?.status === 410 ? e.body.company : undefined;
  return (
    <div className="pl-gone" role="alert" data-testid="pl-gone">
      <CrIcon k="lock" />
      <p>{company ? PARTNER_LINK_MSG.gone(company.name) : e?.status === 404 ? PARTNER_LINK_MSG.notFound : msgOf(error)}</p>
      {company?.phone ? (
        <a className="btn pl-big" href={`tel:${company.phone.replace(/[^0-9+]/g, "")}`} data-testid="pl-gone-phone">
          <CrIcon k="phone" />
          {company.phone}
        </a>
      ) : null}
    </div>
  );
}

type Sheet = { t: "delay"; task: PartnerTask } | { t: "photo"; task: PartnerTask } | null;

function Body({ token, page }: { token: string; page: PageData }) {
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const [sheet, setSheet] = useState<Sheet>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | undefined>();
  const [openNext, setOpenNext] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const reload = () => qc.invalidateQueries({ queryKey: ["partner-page", token] });
  const noMore = page.left.reports <= 0;

  const send = async (task: PartnerTask, kind: ScheduleReportKind, extra: { delayDays?: number; reason?: ScheduleReportReason; note?: string | null } = {}) => {
    setBusy(`${task.rowId}:${kind}`);
    setErr(undefined);
    try {
      const r = await papi<PartnerReport>("POST", `${token}/reports`, { rowId: task.rowId, kind, ...extra });
      await reload();
      toast(PARTNER_LINK_MSG.sent(page.company.name));
      return r;
    } catch (e) {
      setErr(msgOf(e));
      await reload();
      throw e;
    } finally {
      setBusy(null);
    }
  };
  const withdraw = async (id: string) => {
    setErr(undefined);
    try {
      await papi("DELETE", `${token}/reports/${id}`);
      toast(PARTNER_LINK_MSG.withdrawn);
    } catch (e) {
      setErr(msgOf(e));
    }
    await reload();
  };

  const reportsOf = (rowId: string) => page.reports.filter((r) => r.rowId === rowId && r.status !== "withdrawn").slice(0, 3);
  const card = (t: PartnerTask) => (
    <TaskCard
      key={t.rowId}
      t={t}
      reports={reportsOf(t.rowId)}
      token={token}
      busy={busy}
      noMore={noMore}
      onDone={() => void send(t, "done").catch(() => undefined)}
      onStop={() => void send(t, "stop").catch(() => undefined)}
      onDelay={() => setSheet({ t: "delay", task: t })}
      onPhoto={() => setSheet({ t: "photo", task: t })}
      onWithdraw={withdraw}
    />
  );

  return (
    <>
      <section className="pl-band" aria-label={PARTNER_LINK_MSG.todayTitle}>
        <svg className="pl-deco" viewBox="0 0 240 90" aria-hidden="true">
          <path d="M20 88V50l40-30 40 30v38M140 88V58l30-22 30 22v30M60 88V64h16v24M160 88V70h14v18" />
        </svg>
        <h1>{PARTNER_LINK_MSG.todayTitle}</h1>
        <p>
          <b data-testid="pl-today">{mdwLongOf(page.today)}</b>
          <span className="pl-from">{PARTNER_LINK_MSG.from(page.company.name)}</span>
        </p>
      </section>
      <div className="pl-pad">
        {noMore ? (
          <p className="pl-note shu" role="status" data-testid="pl-no-more">
            {PARTNER_LINK_MSG.tooMany}
          </p>
        ) : null}
        <ErrorText>{err}</ErrorText>
        {page.tasks.today.length ? (
          page.tasks.today.map(card)
        ) : (
          <div className="pl-card pl-empty" data-testid="pl-no-today">
            {PARTNER_LINK_MSG.noToday}
          </div>
        )}

        {page.tasks.next.length ? (
          <section className="pl-sec">
            <h2>{PARTNER_LINK_MSG.nextTitle}</h2>
            <div className="pl-list">
              {page.tasks.next.map((t) => (
                <div key={t.rowId}>
                  <button type="button" className="pl-item" aria-expanded={openNext === t.rowId} onClick={() => setOpenNext((v) => (v === t.rowId ? null : t.rowId))} data-testid="pl-next">
                    <span className="ic" aria-hidden="true">
                      <CrIcon k="cal" />
                    </span>
                    <span className="tx">
                      <b>{t.start === t.end ? mdwOf(t.start) : `${mdwOf(t.start)}〜 ${mdwOf(t.end)}`}</b>
                      <small>
                        {t.groupName}・{t.name}
                      </small>
                    </span>
                    <CrIcon k={openNext === t.rowId ? "up" : "right"} />
                  </button>
                  {openNext === t.rowId ? card(t) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {page.tasks.done.length ? (
          <section className="pl-sec">
            <button type="button" className="pl-fold" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)} data-testid="pl-done-fold">
              <CrIcon k={showDone ? "up" : "right"} />
              {PARTNER_LINK_MSG.doneTitle}（{page.tasks.done.length}）
            </button>
            {showDone ? (
              <div className="pl-list">
                {page.tasks.done.map((t) => (
                  <div key={t.rowId} className="pl-item done" data-testid="pl-done">
                    <span className="ic" aria-hidden="true">
                      <CrIcon k="check" />
                    </span>
                    <span className="tx">
                      <b>{t.start === t.end ? mdOf(t.start) : `${mdOf(t.start)}〜${mdOf(t.end)}`}</b>
                      <small>
                        {t.groupName}・{t.name}
                      </small>
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        <footer className="pl-foot">
          <p>
            <CrIcon k="lock" />
            <span data-testid="pl-expires">{PARTNER_LINK_MSG.footer(mdJst(page.expiresAt))}</span>
          </p>
          <p className="muted">{PARTNER_LINK_MSG.footerNote}</p>
        </footer>
      </div>
      {sheet?.t === "delay" ? (
        <DelaySheet
          task={sheet.task}
          company={page.company.name}
          onClose={() => setSheet(null)}
          onSend={async (v) => {
            await send(sheet.task, "delay", v);
            setSheet(null);
          }}
        />
      ) : null}
      {sheet?.t === "photo" ? (
        <PhotoSheet
          task={sheet.task}
          token={token}
          left={Math.min(PARTNER_LINK_LIMITS.photosPerReport, page.left.photos)}
          onClose={() => setSheet(null)}
          onDone={async () => {
            setSheet(null);
            await reload();
            toast(PARTNER_LINK_MSG.sent(page.company.name));
          }}
        />
      ) : null}
      {toastNode}
    </>
  );
}

/** 稼働日の区切りの棒（済んだ日は濃く、今日に印）。10日を超える工程は1本の棒 */
function DaysBar({ t, today }: { t: PartnerTask; today: string }) {
  const ds = t.workDates;
  if (!ds.length) return null;
  if (ds.length > 10) {
    const k = ds.filter((d) => d < today).length;
    return (
      <div className="pl-days long" aria-label={`${mdOf(ds[0]!)}〜${mdOf(ds[ds.length - 1]!)}`}>
        <span className="bar">
          <i style={{ width: `${Math.round((k / ds.length) * 100)}%` }} />
        </span>
        <span className="lb">
          <span>{mdOf(ds[0]!)}</span>
          {t.dayIndex ? <b>{mdOf(today)} 今日</b> : null}
          <span>{mdOf(ds[ds.length - 1]!)}</span>
        </span>
      </div>
    );
  }
  return (
    <div className="pl-days" aria-hidden="true">
      {ds.map((d) => (
        <span key={d} className={"pl-seg" + (d < today ? " past" : d === today ? " today" : "")}>
          <i />
          <small>{d === today ? `${mdOf(d)} 今日` : ds.length <= 5 || d === ds[0] || d === ds[ds.length - 1] ? mdOf(d) : ""}</small>
        </span>
      ))}
    </div>
  );
}

function TaskCard({
  t,
  reports,
  token,
  busy,
  noMore,
  onDone,
  onStop,
  onDelay,
  onPhoto,
  onWithdraw,
}: {
  t: PartnerTask;
  reports: PartnerReport[];
  token: string;
  busy: string | null;
  noMore: boolean;
  onDone: () => void;
  onStop: () => void;
  onDelay: () => void;
  onPhoto: () => void;
  onWithdraw: (id: string) => void;
}) {
  const today = useMemo(() => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10), []);
  const finished = t.progress >= 100;
  const pendingDone = finished || reports.some((r) => r.kind === "done" && r.status === "pending");
  const pendingStop = reports.some((r) => r.kind === "stop" && r.status === "pending" && r.workDate === today);
  const is = (k: ScheduleReportKind) => busy === `${t.rowId}:${k}`;
  return (
    <article className="pl-card" data-testid="pl-card" data-row-id={t.rowId}>
      <div className="pl-chips">
        {t.workToday ? <span className="pl-chip ai">{PARTNER_LINK_MSG.todayChip}</span> : null}
        {t.restToday ? (
          <span className="pl-chip" data-testid="pl-rest-today">
            {PARTNER_LINK_MSG.restTodayChip}
          </span>
        ) : null}
        {t.dayIndex ? <span className="pl-count">{PARTNER_LINK_MSG.dayOf(t.dayIndex, t.workDates.length || t.days)}</span> : null}
        {t.overdue ? <span className="pl-chip shu">{PARTNER_LINK_MSG.overdue(mdOf(t.end))}</span> : null}
        {!t.workToday && !t.overdue ? <span className="pl-count">{t.start === t.end ? mdwOf(t.start) : `${mdwOf(t.start)}〜 ${mdwOf(t.end)}`}</span> : null}
        <span className="sp" />
        {finished ? (
          <span className="pl-chip ai" data-testid="pl-finished">
            {PARTNER_LINK_MSG.finished}
          </span>
        ) : t.lastDay ? (
          <span className="pl-chip">{PARTNER_LINK_MSG.lastDay}</span>
        ) : null}
      </div>
      <h3 data-testid="pl-group">{t.groupName}</h3>
      <p className="pl-task" data-testid="pl-task">
        {t.name}
        {t.crew ? <span className="muted">・{t.crew}人</span> : null}
      </p>
      <DaysBar t={t} today={today} />
      {/* 日のメモ（#299）：その{task}のメモと日付の見出しのメモ（{siteShort}のメモ）。今日から終わりまでの日 */}
      {t.dayNotes?.length ? (
        <ul className="pl-notes" aria-label={PARTNER_LINK_MSG.dayNotesTitle} data-testid="pl-notes">
          {t.dayNotes.map((n) => (
            <li key={`${n.site ? "s" : "r"}${n.day}`} data-testid="pl-note" data-date={n.day}>
              <b>{mdwOf(n.day)}</b>
              {n.site ? <span className="pl-chip">{PARTNER_LINK_MSG.siteNote}</span> : null}
              <span className="tx">{n.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {t.address || t.staffName || t.phone ? (
        <div className="pl-info">
          {t.address ? (
            <div className="row">
              <CrIcon k="pin" />
              <span className="v">{t.address}</span>
              <a className="btn text" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t.address)}`} target="_blank" rel="noreferrer noopener" data-testid="pl-map">
                {PARTNER_LINK_MSG.map}
              </a>
            </div>
          ) : null}
          {t.staffName || t.phone ? (
            <div className="row">
              <CrIcon k="user" />
              <span className="v">
                {t.staffName ?? ""}
                <small>{PARTNER_LINK_MSG.staff}</small>
              </span>
              {t.phone ? (
                <a className="btn text" href={`tel:${t.phone.replace(/[^0-9+]/g, "")}`} data-testid="pl-tel">
                  <CrIcon k="phone" />
                  {PARTNER_LINK_MSG.phone}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="pl-acts">
        <button type="button" className="btn primary" disabled={!t.started || pendingDone || noMore || !!busy} title={!t.started ? PARTNER_LINK_MSG.notStarted : undefined} onClick={onDone} data-testid="pl-done-btn">
          <CrIcon k="check" />
          {is("done") ? "送信中…" : PARTNER_LINK_MSG.done}
        </button>
        <button type="button" className="btn tonal" disabled={!t.workToday || finished || pendingStop || noMore || !!busy} title={!t.workToday ? PARTNER_LINK_MSG.notWorkDay : undefined} onClick={onStop} data-testid="pl-stop-btn">
          <CrIcon k={stopIcon()} />
          {is("stop") ? "送信中…" : PARTNER_LINK_MSG.stop}
        </button>
        <button type="button" className="btn" disabled={finished || noMore || !!busy} onClick={onDelay} data-testid="pl-delay-btn">
          <CrIcon k="clock" />
          {PARTNER_LINK_MSG.delay}
        </button>
        <button type="button" className="btn" disabled={noMore || !!busy} onClick={onPhoto} data-testid="pl-photo-btn">
          <CrIcon k="camera" />
          {PARTNER_LINK_MSG.photo}
        </button>
      </div>
      {!t.started ? <p className="hint pl-hint">{PARTNER_LINK_MSG.notStarted}</p> : null}
      {reports.map((r) => (
        <ReportLine key={r.id} r={r} token={token} onWithdraw={() => onWithdraw(r.id)} />
      ))}
    </article>
  );
}

/** 送った報告の今の状態（確かめ待ち → 反映しました／見送りました） */
function ReportLine({ r, token, onWithdraw }: { r: PartnerReport; token: string; onWithdraw: () => void }) {
  const label = tx(reportKindText({ kind: r.kind, delayDays: r.delayDays, photoCount: r.photos.length }));
  return (
    <div className={"pl-status " + r.status} data-testid="pl-status" data-kind={r.kind} data-status={r.status}>
      <CrIcon k={r.status === "applied" ? "check" : r.status === "dismissed" ? "warn" : "clock"} />
      <span className="tx">
        {r.status === "pending" ? PARTNER_LINK_MSG.statusPending(label, hmJst(r.createdAt)) : r.status === "applied" ? PARTNER_LINK_MSG.statusApplied(label) : PARTNER_LINK_MSG.statusDismissed(label)}
        {r.status === "dismissed" && r.decidedNote ? <small data-testid="pl-dismiss-note">{r.decidedNote}</small> : null}
        {r.kind === "photo" && r.photos.length ? (
          <span className="pl-thumbs">
            {r.photos.map((p) => (
              <img key={p.id} src={`/api/v1/p/${token}/photos/${p.id}?size=thumb`} alt="" referrerPolicy="no-referrer" width={40} height={40} />
            ))}
          </span>
        ) : null}
      </span>
      {r.status === "pending" ? (
        <button type="button" className="btn text" onClick={onWithdraw} data-testid="pl-withdraw">
          {PARTNER_LINK_MSG.withdraw}
        </button>
      ) : null}
    </div>
  );
}

/** 下から出るシート（Esc・後ろを押すと閉じる） */
function Sheet({ title, sub, onClose, children, testId }: { title: string; sub: string; onClose: () => void; children: ReactNode; testId: string }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="dim sch-sheetdim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sch-sheet pl-sheet" role="dialog" aria-modal="true" aria-label={title} data-testid={testId} data-notice-above="">
        <span className="grab" aria-hidden="true" />
        <div className="hd">
          <b>{title}</b>
          <small>{sub}</small>
        </div>
        {children}
      </div>
    </div>
  );
}

function DelaySheet({ task, company, onClose, onSend }: { task: PartnerTask; company: string; onClose: () => void; onSend: (v: { delayDays: number; reason: ScheduleReportReason; note: string | null }) => Promise<void> }) {
  const [days, setDays] = useState(1);
  const [more, setMore] = useState(false);
  const [reason, setReason] = useState<ScheduleReportReason | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  return (
    <Sheet title={PARTNER_LINK_MSG.delayTitle} sub={`${task.groupName}・${task.name}`} onClose={onClose} testId="pl-delay-sheet">
      <p className="pl-lbl">{PARTNER_LINK_MSG.delayHowMany}</p>
      <div className="pl-picks four" role="radiogroup" aria-label={PARTNER_LINK_MSG.delayHowMany}>
        {[1, 2, 3].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={!more && days === n} className={"btn" + (!more && days === n ? " primary" : "")} onClick={() => (setMore(false), setDays(n))} data-testid={`pl-delay-${n}`}>
            {PARTNER_LINK_MSG.delayDays(n)}
          </button>
        ))}
        <button type="button" role="radio" aria-checked={more} className={"btn" + (more ? " primary" : "")} onClick={() => (setMore(true), setDays((d) => Math.max(4, d)))} data-testid="pl-delay-more">
          {PARTNER_LINK_MSG.delayMore}
        </button>
      </div>
      {more ? (
        <div className="pl-stepper">
          <button type="button" className="btn" aria-label="1日減らす" disabled={days <= 4} onClick={() => setDays((d) => Math.max(4, d - 1))}>
            −
          </button>
          <b data-testid="pl-delay-value">{PARTNER_LINK_MSG.delayDays(days)}</b>
          <button type="button" className="btn" aria-label="1日増やす" disabled={days >= PARTNER_LINK_LIMITS.delayMax} onClick={() => setDays((d) => Math.min(PARTNER_LINK_LIMITS.delayMax, d + 1))}>
            ＋
          </button>
        </div>
      ) : null}
      <p className="pl-lbl">{PARTNER_LINK_MSG.reasonTitle}</p>
      <div className="pl-reasons" role="radiogroup" aria-label={PARTNER_LINK_MSG.reasonTitle}>
        {REPORT_REASONS.map((k) => (
          <button key={k} type="button" role="radio" aria-checked={reason === k} className={"pl-reason" + (reason === k ? " on" : "")} onClick={() => setReason(k)} data-testid={`pl-reason-${k}`}>
            {REPORT_REASON_LABEL[k]}
          </button>
        ))}
      </div>
      <label className="pl-lbl" htmlFor="pl-delay-note">
        {PARTNER_LINK_MSG.noteTitle}
      </label>
      <input id="pl-delay-note" className="inp pl-in" maxLength={PARTNER_LINK_LIMITS.note} value={note} onChange={(e) => setNote(e.target.value)} data-testid="pl-delay-note" />
      <p className="hint">{PARTNER_LINK_MSG.sendFoot(company)}</p>
      <ErrorText>{err}</ErrorText>
      <button
        type="button"
        className="btn primary pl-big"
        disabled={!reason || busy}
        title={reason ? undefined : PARTNER_LINK_MSG.pickReason}
        onClick={async () => {
          if (!reason) return;
          setBusy(true);
          setErr(undefined);
          try {
            await onSend({ delayDays: days, reason, note: note.trim() || null });
          } catch (e) {
            setErr(msgOf(e));
            setBusy(false);
          }
        }}
        data-testid="pl-delay-send"
      >
        {PARTNER_LINK_MSG.send}
      </button>
      <button type="button" className="btn pl-big" onClick={onClose}>
        {PARTNER_LINK_MSG.cancel}
      </button>
    </Sheet>
  );
}

/* ---------------- 写真（この端末で描き直して小さくする。撮った場所などの情報は描き直すと消える） ---------------- */
type Picked = { id: string; preview: string; display: string; thumb: string };
const blobToDataUrl = (b: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error(PARTNER_LINK_MSG.photoBroken));
    r.readAsDataURL(b);
  });
async function drawJpeg(src: ImageBitmap, max: number, quality: number): Promise<string> {
  const { width, height } = fitWithin(src.width, src.height, max);
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  c.getContext("2d")!.drawImage(src, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => c.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error(PARTNER_LINK_MSG.photoBroken);
  return blobToDataUrl(blob);
}
async function preparePhoto(file: File): Promise<Picked> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(PARTNER_LINK_MSG.photoBroken);
  }
  try {
    let display = await drawJpeg(bmp, PARTNER_LINK_LIMITS.resizeDisplayPx, 0.8);
    if ((display.length * 3) / 4 > PARTNER_LINK_LIMITS.displayBytes) display = await drawJpeg(bmp, PARTNER_LINK_LIMITS.resizeDisplayPx, 0.6);
    const thumb = await drawJpeg(bmp, PARTNER_LINK_LIMITS.resizeThumbPx, 0.8);
    const b64 = (u: string) => u.slice(u.indexOf(",") + 1);
    return { id: crypto.randomUUID(), preview: thumb, display: b64(display), thumb: b64(thumb) };
  } finally {
    bmp.close();
  }
}

function PhotoSheet({ task, token, left, onClose, onDone }: { task: PartnerTask; token: string; left: number; onClose: () => void; onDone: () => Promise<void> }) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ i: number; n: number } | null>(null);
  const [err, setErr] = useState<string | undefined>();
  const input = useRef<HTMLInputElement>(null);
  const max = Math.max(0, left);
  const room = max - picked.length;
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setErr(undefined);
    const list = [...files].slice(0, room);
    for (const f of list) {
      try {
        const p = await preparePhoto(f);
        setPicked((xs) => (xs.length >= max ? xs : [...xs, p]));
      } catch (e) {
        setErr(msgOf(e));
      }
    }
    if (input.current) input.current.value = "";
  };
  const submit = async () => {
    setBusy(true);
    setErr(undefined);
    let reportId: string | null = null;
    let ok = 0;
    try {
      const r = await papi<PartnerReport>("POST", `${token}/reports`, { rowId: task.rowId, kind: "photo", note: note.trim() || null });
      reportId = r.id;
      for (let i = 0; i < picked.length; i++) {
        setProgress({ i: i + 1, n: picked.length });
        await papi("POST", `${token}/reports/${r.id}/photos`, { display: picked[i]!.display, thumb: picked[i]!.thumb });
        ok++;
      }
      if (!ok) throw new Error(PARTNER_LINK_MSG.photoBroken);
      await onDone();
    } catch (e) {
      // 1枚も送れなかったら報告ごと取り消す（写真の無い写真の報告を残さない）
      if (reportId && !ok) await papi("DELETE", `${token}/reports/${reportId}`).catch(() => undefined);
      if (ok) await onDone();
      setErr(msgOf(e));
      setBusy(false);
      setProgress(null);
    }
  };
  return (
    <Sheet title={PARTNER_LINK_MSG.photoTitle} sub={`${task.groupName}・${task.name}`} onClose={busy ? () => undefined : onClose} testId="pl-photo-sheet">
      <div className="pl-photos">
        {picked.map((p) => (
          <span key={p.id} className="pl-ph" data-testid="pl-photo-picked">
            <img src={p.preview} alt="" />
            {busy ? null : (
              <button type="button" aria-label={PARTNER_LINK_MSG.photoRemove} onClick={() => setPicked((xs) => xs.filter((x) => x.id !== p.id))}>
                <CrIcon k="close" />
              </button>
            )}
          </span>
        ))}
        {room > 0 && !busy ? (
          <label className="pl-ph add" data-testid="pl-photo-add">
            <input ref={input} className="file-hidden" type="file" accept="image/*" multiple onChange={(e) => void onFiles(e.target.files)} data-testid="pl-photo-input" />
            <CrIcon k="plus" />
            <small>{PARTNER_LINK_MSG.photoPick}</small>
          </label>
        ) : null}
      </div>
      <p className="hint" data-testid="pl-photo-left">
        {PARTNER_LINK_MSG.photoLeft(Math.max(0, room))}
      </p>
      <label className="pl-lbl" htmlFor="pl-photo-note">
        {PARTNER_LINK_MSG.noteTitle}
      </label>
      <input id="pl-photo-note" className="inp pl-in" maxLength={PARTNER_LINK_LIMITS.note} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} data-testid="pl-photo-note" />
      <p className="hint">{PARTNER_LINK_MSG.photoNote}</p>
      <ErrorText>{err}</ErrorText>
      <button type="button" className="btn primary pl-big" disabled={!picked.length || busy} onClick={() => void submit()} data-testid="pl-photo-send">
        {progress ? PARTNER_LINK_MSG.photoSending(progress.i, progress.n) : PARTNER_LINK_MSG.photoSend(picked.length)}
      </button>
      <button type="button" className="btn pl-big" disabled={busy} onClick={onClose}>
        {PARTNER_LINK_MSG.cancel}
      </button>
    </Sheet>
  );
}
